import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export default function ProvisionPartnerDrawer({ open, onClose, onSuccess }: Props) {
    const { fetchWithAuth } = useAuth();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<{ clientId: string; clientSecret: string } | null>(null);
    const [form, setForm] = useState({
        organizationName: '',
        applicationName: '',
        appKey: '',
        subdomain: '',
        adminEmail: '',
    });

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const res = await fetchWithAuth('/api/partners/provision', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    organizationName: form.organizationName,
                    applicationName: form.applicationName,
                    appKey: form.appKey,
                    subdomain: form.subdomain || undefined,
                    adminEmail: form.adminEmail,
                    services: ['rest-api', 'mcp'],
                }),
            });
            const data = await res.json();
            if (res.ok) {
                setResult({ clientId: data.clientId, clientSecret: data.clientSecret });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (result) onSuccess();
        else onClose();
        setResult(null);
        setForm({ organizationName: '', applicationName: '', appKey: '', subdomain: '', adminEmail: '' });
    };

    return (
        <Sheet open={open} onOpenChange={handleClose}>
            <SheetContent className="sm:max-w-md">
                <SheetHeader>
                    <SheetTitle>Provision Partner</SheetTitle>
                    <SheetDescription>
                        Create a new white-label partner with M2M credentials.
                    </SheetDescription>
                </SheetHeader>

                {result ? (
                    <div className="space-y-4 py-4">
                        <div className="rounded-md bg-green-50 dark:bg-green-950 p-4 text-sm">
                            <p className="font-medium text-green-800 dark:text-green-200 mb-2">Partner provisioned successfully!</p>
                            <p className="text-green-700 dark:text-green-300 mb-1">Save these credentials — the secret will not be shown again.</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Client ID</Label>
                            <Input readOnly value={result.clientId} className="font-mono text-xs" />
                        </div>
                        <div className="space-y-2">
                            <Label>Client Secret</Label>
                            <Input readOnly value={result.clientSecret} className="font-mono text-xs" />
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Organization Name</Label>
                            <Input value={form.organizationName} onChange={(e) => setForm(f => ({ ...f, organizationName: e.target.value }))} placeholder="Acme AI" />
                        </div>
                        <div className="space-y-2">
                            <Label>Application Name</Label>
                            <Input value={form.applicationName} onChange={(e) => setForm(f => ({ ...f, applicationName: e.target.value }))} placeholder="Acme Platform" />
                        </div>
                        <div className="space-y-2">
                            <Label>App Key</Label>
                            <Input value={form.appKey} onChange={(e) => setForm(f => ({ ...f, appKey: e.target.value }))} placeholder="acme-platform" />
                        </div>
                        <div className="space-y-2">
                            <Label>Subdomain (optional)</Label>
                            <Input value={form.subdomain} onChange={(e) => setForm(f => ({ ...f, subdomain: e.target.value }))} placeholder="acme" />
                        </div>
                        <div className="space-y-2">
                            <Label>Admin Email</Label>
                            <Input value={form.adminEmail} onChange={(e) => setForm(f => ({ ...f, adminEmail: e.target.value }))} placeholder="admin@acme.ai" type="email" />
                        </div>
                    </div>
                )}

                <SheetFooter>
                    {result ? (
                        <Button onClick={handleClose}>Done</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={onClose}>Cancel</Button>
                            <Button onClick={handleSubmit} disabled={loading || !form.organizationName || !form.appKey || !form.adminEmail}>
                                {loading ? 'Provisioning...' : 'Provision'}
                            </Button>
                        </>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}
